/**
 * The game under the strip board (ISSUE-1298), after v1's PoolBoxScore: the
 * header (away at home, the full kickoff time in the fan's own zone, the
 * status and the score from `/strips/card-get`), then the game's line score,
 * reusing game detail's own `LineScoreSection` from `/event/detail-get`. It
 * refreshes at the detail's own cadence while the game is live, so members
 * can follow the score that decides the digits. A link opens the full game.
 */
import { useEventDetailGet } from "@sportsmet/events-sdk/private";
import { Card, Skeleton } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { LineScoreSection } from "../gameDetail/LineScoreSection";
import type { typeMatchupTeamMark } from "../gameDetail/matchupTypes";
import { resolvePlatePair } from "../gameDetail/teamPlateText";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import {
  stripRefreshIntervalMs,
  type typeStripGameView,
} from "./stripBoardModel";
import {
  STRIP_BOX_SCORE_UNAVAILABLE,
  STRIP_CUSTOM_GAME_WINNERS_NOTE,
  STRIP_FULL_GAME_LABEL,
  stripGameHeading,
} from "./stripCardCopy";
import { stripKickoffLabel } from "./stripTime";

export const StripGameHeader = ({
  game,
}: {
  game: typeStripGameView;
}): ReactNode => {
  const timeZone = resolveViewerTimeZone();
  const hasScore =
    typeof game.awayScore === "number" && typeof game.homeScore === "number";
  if (game.isCustom) {
    // ISSUE-1299. No teams, no clock and no feed: the name and how winners get picked.
    return (
      <div
        data-testid="strip-game-header"
        className="flex flex-col items-center gap-1 text-center"
      >
        <h2 className="text-body-strong text-ink">{game.label}</h2>
        <p className="text-caption text-ink-secondary">
          {STRIP_CUSTOM_GAME_WINNERS_NOTE}
        </p>
      </div>
    );
  }
  return (
    <div
      data-testid="strip-game-header"
      className="flex flex-col items-center gap-1 text-center"
    >
      <h2 className="text-body-strong text-ink">
        {stripGameHeading({
          awayTeamName: game.awayTeamName,
          homeTeamName: game.homeTeamName,
        })}
      </h2>
      <p className="text-caption text-ink-secondary">
        {stripKickoffLabel({ kickoffAt: game.kickoffAt, timeZone })}
      </p>
      {hasScore ? (
        <p
          className="text-body-strong text-ink tabular-nums"
          data-testid="strip-game-score"
        >
          {`${game.awayTeamAbbreviation} ${String(game.awayScore)}, ${game.homeTeamAbbreviation} ${String(game.homeScore)}`}
          <span className="text-caption text-ink-secondary">{` · ${game.statusLabel}`}</span>
        </p>
      ) : (
        <p className="text-caption text-ink-secondary">{game.statusLabel}</p>
      )}
    </div>
  );
};

export const StripLineScore = ({ eventId }: { eventId: string }): ReactNode => {
  const timeZone = resolveViewerTimeZone();
  const detail = useEventDetailGet(
    { eventId, timeZone },
    {
      refetchInterval: (query) => {
        const data = query.state.data?.data;
        if (data?.isLive !== true) return false;
        return stripRefreshIntervalMs(data.refreshAfterSeconds);
      },
    },
  );

  if (detail.isPending) {
    return <Skeleton shape="card" />;
  }
  const data = detail.data?.data ?? null;
  const lineScore =
    data?.matchup?.sections.find((section) => section.kind === "lineScore")
      ?.lineScore ?? null;

  return (
    <div className="flex flex-col gap-2" data-testid="strip-line-score">
      {data === null || lineScore === null ? (
        <Card>
          <p className="text-body text-ink-secondary">
            {STRIP_BOX_SCORE_UNAVAILABLE}
          </p>
        </Card>
      ) : (
        <LineScoreSection
          lineScore={lineScore}
          away={teamMark(data.away, data.home, "away")}
          home={teamMark(data.away, data.home, "home")}
          isLive={data.isLive}
        />
      )}
      <Link
        to="/scores"
        search={{ eventId }}
        className="inline-flex min-h-touch-target items-center self-center text-body-strong text-primary underline underline-offset-4"
        data-testid="strip-full-game-link"
      >
        {STRIP_FULL_GAME_LABEL}
      </Link>
    </div>
  );
};

type typeDetailTeam = {
  teamId: string;
  name: string;
  abbreviation: string;
  logoUrl?: string | null;
};

function teamMark(
  away: typeDetailTeam,
  home: typeDetailTeam,
  side: "away" | "home",
): typeMatchupTeamMark {
  const { awayText, homeText } = resolvePlatePair({
    away: { name: away.name, abbreviation: away.abbreviation },
    home: { name: home.name, abbreviation: home.abbreviation },
  });
  const team = side === "away" ? away : home;
  return {
    name: team.name,
    plateText: side === "away" ? awayText : homeText,
    logoUrl: team.logoUrl ?? null,
    teamId: team.teamId,
    abbreviation: team.abbreviation,
  };
}
